export type CarrierServicePickerItem = {
  courierServiceId: string;
  carrier: string;
  service: string;
  displayName?: string;
  active?: boolean | null;
  domestic?: boolean | null;
  international?: boolean | null;
};

type MaybeCarrierServicePickerItem =
  | CarrierServicePickerItem
  | null
  | undefined;

export type SearchablePickerOption = {
  value: string;
  label: string;
  description?: string;
  recommended?: boolean;
};

const normalizeText = (value: unknown) => String(value || '').trim();
const normalizeComparableText = (value: unknown) =>
  normalizeText(value).toLowerCase();

const getValidServices = (
  services: MaybeCarrierServicePickerItem[] | null | undefined = [],
) =>
  (services ?? []).filter((service): service is CarrierServicePickerItem =>
    Boolean(service),
  );

const getServiceLabel = (service: CarrierServicePickerItem) =>
  normalizeText(service.service) ||
  normalizeText(service.displayName) ||
  normalizeText(service.courierServiceId);

const isUsps = (service: CarrierServicePickerItem) =>
  normalizeComparableText(service.carrier) === 'usps';

const isGroundService = (service: CarrierServicePickerItem) => {
  const label = normalizeComparableText(getServiceLabel(service));

  return label.includes('ground advantage') || /\bground\b/.test(label);
};

const isFastService = (service: CarrierServicePickerItem) => {
  const label = normalizeComparableText(getServiceLabel(service));

  return (
    label.includes('express') ||
    label.includes('priority') ||
    label.includes('next day') ||
    label.includes('2nd day') ||
    label.includes('second day')
  );
};

const serviceRank = (service: CarrierServicePickerItem) => {
  const label = normalizeComparableText(getServiceLabel(service));
  let score = 0;

  if (isUsps(service)) score += 1000;
  if (label.includes('ground advantage')) score += 500;
  else if (isGroundService(service)) score += 300;
  if (service.domestic === true) score += 80;
  if (service.active !== false) score += 20;
  if (label.includes('international')) score -= 250;
  if (isFastService(service) && !isGroundService(service)) score -= 100;
  if (label.includes('envelope') || label.includes('letter')) score -= 75;

  return score;
};

const isRecommendedService = (service: CarrierServicePickerItem) =>
  isUsps(service) && isGroundService(service);

const getServiceDescription = (
  service: CarrierServicePickerItem,
  recommended: boolean,
) => {
  if (recommended) return 'Recommended for most U.S. returns';
  if (isUsps(service) && isFastService(service)) return 'Fastest USPS option';
  if (service.domestic && service.international) {
    return 'Domestic and international';
  }
  if (service.domestic) return 'Domestic';
  if (service.international) return 'International';

  return undefined;
};

const dedupeServicesByLabel = (services: CarrierServicePickerItem[]) => {
  const byLabel = services.reduce<Record<string, CarrierServicePickerItem>>(
    (map, service) => {
      const key = normalizeComparableText(getServiceLabel(service));
      const current = map[key];

      if (!current || serviceRank(service) > serviceRank(current)) {
        map[key] = service;
      }

      return map;
    },
    {},
  );

  return Object.values(byLabel);
};

const dedupeServicesByCarrierAndLabel = (
  services: CarrierServicePickerItem[],
) => {
  const byCarrierAndLabel = services.reduce<
    Record<string, CarrierServicePickerItem>
  >((map, service) => {
    const key = [
      normalizeComparableText(service.carrier),
      normalizeComparableText(getServiceLabel(service)),
    ].join(':');
    const current = map[key];

    if (!current || serviceRank(service) > serviceRank(current)) {
      map[key] = service;
    }

    return map;
  }, {});

  return Object.values(byCarrierAndLabel);
};

export const getCarrierOptions = (
  services: MaybeCarrierServicePickerItem[] | null | undefined = [],
): SearchablePickerOption[] => {
  const counts = dedupeServicesByCarrierAndLabel(
    getValidServices(services),
  ).reduce<Record<string, number>>((map, service) => {
    const carrier = normalizeText(service.carrier);
    if (!carrier) return map;

    map[carrier] = (map[carrier] || 0) + 1;
    return map;
  }, {});

  return Object.entries(counts)
    .sort(([leftCarrier], [rightCarrier]) =>
      leftCarrier.localeCompare(rightCarrier),
    )
    .map(([carrier, count]) => ({
      value: carrier,
      label: carrier,
      description:
        normalizeComparableText(carrier) === 'usps'
          ? 'Recommended for most U.S. returns'
          : `${count} service${count === 1 ? '' : 's'}`,
    }));
};

export const getServiceOptionsForCarrier = (
  services: MaybeCarrierServicePickerItem[] | null | undefined = [],
  carrier: string,
): SearchablePickerOption[] => {
  const normalizedCarrier = normalizeText(carrier);
  const carrierServices = getValidServices(services).filter(
    (service) => normalizeText(service.carrier) === normalizedCarrier,
  );

  return dedupeServicesByLabel(carrierServices)
    .sort((left, right) => {
      const rankDifference = serviceRank(right) - serviceRank(left);

      return (
        rankDifference ||
        getServiceLabel(left).localeCompare(getServiceLabel(right))
      );
    })
    .map((service) => {
      const label = getServiceLabel(service);
      const courierServiceId = normalizeText(service.courierServiceId);
      const recommended = isRecommendedService(service);

      return removeUndefinedValues({
        value: courierServiceId,
        label,
        description: getServiceDescription(service, recommended),
        recommended: recommended || undefined,
      });
    });
};

export const getRecommendedServiceSelection = (
  services: MaybeCarrierServicePickerItem[] | null | undefined = [],
) => {
  const recommendedService = getValidServices(services)
    .filter((service) => normalizeText(service.carrier))
    .slice()
    .sort((left, right) => {
      const rankDifference = serviceRank(right) - serviceRank(left);

      return (
        rankDifference ||
        getServiceLabel(left).localeCompare(getServiceLabel(right))
      );
    })[0];

  if (!recommendedService) return null;

  return {
    carrier: normalizeText(recommendedService.carrier),
    courierServiceId: normalizeText(recommendedService.courierServiceId),
  };
};

function removeUndefinedValues<T extends Record<string, any>>(input: T) {
  return Object.fromEntries(
    Object.entries(input).filter(([, value]) => value !== undefined),
  ) as T;
}
